/** Window event name dApps dispatch to request Dusk provider announcements. */
export const DUSK_REQUEST_PROVIDER_EVENT = "dusk:requestProvider";
/** Window event name wallets dispatch to announce a Dusk provider. */
export const DUSK_ANNOUNCE_PROVIDER_EVENT = "dusk:announceProvider";
/** Default localStorage key for remembering the selected wallet provider. */
export const DUSK_SELECTED_PROVIDER_STORAGE_KEY = "dusk.connect.selectedProvider";
/** Provider info fields required by Dusk provider discovery. */
export const DUSK_PROVIDER_INFO_FIELDS = ["uuid", "name", "icon", "rdns"];
function trim(value) {
    return typeof value === "string" ? value.trim() : "";
}
function cloneInfo(info) {
    return {
        uuid: info.uuid,
        name: info.name,
        icon: info.icon,
        rdns: info.rdns,
    };
}
/** Return true when a value satisfies the Dusk injected-provider shape. */
export function isDuskProvider(value) {
    return (value &&
        typeof value === "object" &&
        value.isDusk === true &&
        typeof value.request === "function" &&
        typeof value.on === "function");
}
/** Return true when a value contains valid Dusk provider metadata. */
export function isDuskProviderInfo(value) {
    return (value &&
        typeof value === "object" &&
        trim(value.uuid).length > 0 &&
        trim(value.name).length > 0 &&
        typeof value.icon === "string" &&
        trim(value.rdns).length > 0);
}
/** Return true when a discovery payload pairs provider metadata with a provider. */
export function isDuskProviderDetail(value) {
    return (value &&
        typeof value === "object" &&
        isDuskProviderInfo(value.info) &&
        isDuskProvider(value.provider));
}
/** Normalize provider metadata for comparison and display. */
export function normalizeDuskProviderInfo(info) {
    return {
        uuid: trim(info.uuid),
        name: trim(info.name),
        icon: String(info.icon ?? ""),
        rdns: trim(info.rdns).toLowerCase(),
    };
}
/** Normalize a full provider discovery payload. */
export function normalizeDuskProviderDetail(detail) {
    return {
        info: normalizeDuskProviderInfo(detail.info),
        provider: detail.provider,
    };
}
/** Create a provider-announcement event for wallet implementations. */
export function makeDuskAnnounceProviderEvent(detail) {
    return new CustomEvent(DUSK_ANNOUNCE_PROVIDER_EVENT, {
        detail: normalizeDuskProviderDetail(detail),
    });
}
/** Create a provider-request event for dApps. */
export function makeDuskRequestProviderEvent() {
    return new Event(DUSK_REQUEST_PROVIDER_EVENT);
}
/** Announce a wallet provider on `window`. */
export function announceDuskProvider(detail) {
    if (typeof window === "undefined")
        return;
    window.dispatchEvent(makeDuskAnnounceProviderEvent(detail));
}
/** Request currently available Dusk providers and collect their announcements. */
export function requestDuskProviders(opts = {}) {
    if (typeof window === "undefined")
        return Promise.resolve([]);
    const timeoutMs = Math.max(0, opts.timeoutMs ?? 40);
    return new Promise((resolve) => {
        const byId = new Map();
        let timer = 0;
        const finish = () => {
            window.removeEventListener(DUSK_ANNOUNCE_PROVIDER_EVENT, onAnnounce);
            if (timer)
                window.clearTimeout(timer);
            opts.signal?.removeEventListener("abort", finish);
            resolve([...byId.values()]
                .map((detail) => ({ info: cloneInfo(detail.info), provider: detail.provider }))
                .sort((a, b) => a.info.name.localeCompare(b.info.name)));
        };
        const onAnnounce = (event) => {
            const detail = event.detail;
            if (!isDuskProviderDetail(detail))
                return;
            const normalized = normalizeDuskProviderDetail(detail);
            if (!normalized.info.uuid)
                return;
            byId.set(normalized.info.uuid, normalized);
        };
        if (opts.signal?.aborted) {
            finish();
            return;
        }
        window.addEventListener(DUSK_ANNOUNCE_PROVIDER_EVENT, onAnnounce);
        opts.signal?.addEventListener("abort", finish, { once: true });
        window.dispatchEvent(makeDuskRequestProviderEvent());
        if (timeoutMs === 0) {
            queueMicrotask(finish);
            return;
        }
        timer = window.setTimeout(finish, timeoutMs);
    });
}
/** Poll discovery until providers are found or the timeout expires. */
export async function waitForDuskProviders(opts = {}) {
    const timeoutMs = opts.timeoutMs ?? 2_000;
    const intervalMs = opts.intervalMs ?? 50;
    const immediate = await requestDuskProviders({ timeoutMs: 0 });
    if (immediate.length || typeof window === "undefined" || timeoutMs <= 0)
        return immediate;
    const started = Date.now();
    for (;;) {
        const remaining = timeoutMs - (Date.now() - started);
        if (remaining <= 0)
            return [];
        const details = await requestDuskProviders({ timeoutMs: Math.min(intervalMs, remaining) });
        if (details.length)
            return details;
    }
}
/** Subscribe to future Dusk provider announcements. */
export function subscribeDuskProviders(listener, options = {}) {
    if (typeof window === "undefined")
        return () => { };
    const onAnnounce = (event) => {
        const detail = event.detail;
        if (!isDuskProviderDetail(detail))
            return;
        listener(normalizeDuskProviderDetail(detail));
    };
    window.addEventListener(DUSK_ANNOUNCE_PROVIDER_EVENT, onAnnounce);
    if (options.requestOnStart !== false) {
        window.dispatchEvent(makeDuskRequestProviderEvent());
    }
    return () => {
        window.removeEventListener(DUSK_ANNOUNCE_PROVIDER_EVENT, onAnnounce);
    };
}
//# sourceMappingURL=discovery.js.map